import { useEffect, useRef } from 'react';

interface LayoutShiftOptions {
  resizeSelectors?: string[];
  toggleSelectors?: string[];
}

/**
 * Hook that detects layout shifts caused by sidebar collapse/expand, drawer open/close, resize, etc.
 * and triggers a callback. Useful for components that need to recalculate their size when the page layout changes.
 * It works by:
 * - Listening for clicks on toggle buttons
 * - Observing resize events on resizable containers
 */
export function useLayoutShiftAwareness(
  onLayoutShift: () => void,
  options: LayoutShiftOptions = {},
) {
  const {
    resizeSelectors = [
      '[data-testid="collapsible-resizable-container"]',
      '[data-resize-handle="true"]',
    ],
    toggleSelectors = [
      '[data-testid="collapse-toggle-close"]',
      '[data-testid="collapse-toggle-open"]',
      '.shared-collapse-toggle',
      '[data-testid="side-nav"]',
      '[data-testid="ai-assistant-toggle"]',
      '[data-testid="CloseIcon"]',
    ],
  } = options;

  const onLayoutShiftRef = useRef(onLayoutShift);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    onLayoutShiftRef.current = onLayoutShift;
  }, [onLayoutShift]);

  // Handle toggle clicks
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isTargetElement = toggleSelectors.some((selector) => target.closest(selector));

      if (isTargetElement) {
        // Wait for transitions to complete
        setTimeout(() => {
          requestAnimationFrame(() => {
            onLayoutShiftRef.current();
          });
        }, 300);
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [toggleSelectors]);

  // Handle resize events by detecting mousedown on resize handles
  useEffect(() => {
    if (resizeSelectors.length === 0) return;

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Check if the target or any parent has the data-resize-handle attribute
      const resizeHandle = target.closest('[data-resize-handle="true"]');
      if (resizeHandle) {
        isDraggingRef.current = true;
      }
    };

    const handleMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        // Wait for transitions to complete
        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              onLayoutShiftRef.current();
            });
          });
        }, 300);
      }
    };

    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizeSelectors]);
}
